import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { categories, mainSiteUrl } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location]);

  const isTransparent = !scrolled && isHome;

  const handleGoHome = () => {
    if (isHome) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isTransparent
          ? "bg-black/25 backdrop-blur-sm"
          : "bg-background-50/95 backdrop-blur-md border-b border-foreground-100/20 shadow-sm"
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <button
            onClick={handleGoHome}
            className={`text-lg font-heading font-bold whitespace-nowrap cursor-pointer transition-colors duration-300 ${
              isTransparent ? "text-background-50" : "text-foreground-950"
            }`}
          >
            superbuy<span className={isTransparent ? "text-primary-300" : "text-primary-500"}>.</span>beer
          </button>

          <div className="hidden md:flex items-center gap-1">
            <button
              onClick={handleGoHome}
              className={`px-3 py-2 text-sm rounded-md whitespace-nowrap cursor-pointer transition-colors ${
                isTransparent
                  ? "text-background-50 hover:text-background-50/80"
                  : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              Home
            </button>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1 px-3 py-2 text-sm rounded-md whitespace-nowrap cursor-pointer transition-colors ${
                  isTransparent
                    ? "text-background-50 hover:text-background-50/80"
                    : "text-foreground-700 hover:text-foreground-950"
                }`}
              >
                Categories
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full right-0 mt-1 w-72 bg-background-50 border border-foreground-100/20 rounded-lg py-2 max-h-96 overflow-y-auto shadow-lg">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 flex-shrink-0">
                        <i className={`${cat.icon} text-foreground-500 text-sm`}></i>
                      </span>
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`px-3 py-2 text-sm rounded-md whitespace-nowrap cursor-pointer transition-colors ${
                isTransparent
                  ? "text-background-50 hover:text-background-50/80"
                  : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              Shopping Guide
            </a>
            <a
              href="#safety"
              className={`px-3 py-2 text-sm rounded-md whitespace-nowrap cursor-pointer transition-colors ${
                isTransparent
                  ? "text-background-50 hover:text-background-50/80"
                  : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              Safety
            </a>
            <a
              href="#faq"
              className={`px-3 py-2 text-sm rounded-md whitespace-nowrap cursor-pointer transition-colors ${
                isTransparent
                  ? "text-background-50 hover:text-background-50/80"
                  : "text-foreground-700 hover:text-foreground-950"
              }`}
            >
              FAQ
            </a>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="ml-3 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              isTransparent ? "text-background-50" : "text-foreground-950"
            }`}
          >
            <i className={`text-xl ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-foreground-100/20 py-3 pb-6 max-h-[80vh] overflow-y-auto">
            <div className="flex flex-col gap-1">
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleGoHome();
                }}
                className="px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer font-medium text-left"
              >
                <span className="w-4 h-4 inline-flex items-center justify-center mr-2">
                  <i className="ri-home-line text-foreground-500"></i>
                </span>
                Home
              </button>

              <div className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider mt-2">
                Categories
              </div>
              <div className="grid grid-cols-2 gap-1 px-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 px-2 py-2.5 text-xs text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer"
                  >
                    <span className="w-6 h-6 flex items-center justify-center rounded-md bg-background-100 flex-shrink-0">
                      <i className={`${cat.icon} text-foreground-400 text-xs`}></i>
                    </span>
                    <span className="leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>

              <div className="border-t border-foreground-100/20 mt-2 pt-2">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer flex items-center gap-2"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-guide-line text-foreground-500"></i>
                  </span>
                  Shopping Guide
                </a>
                <a
                  href="#safety"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer flex items-center gap-2"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-shield-check-line text-foreground-500"></i>
                  </span>
                  Safety
                </a>
                <a
                  href="#faq"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 rounded-md cursor-pointer flex items-center gap-2"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-question-line text-foreground-500"></i>
                  </span>
                  FAQ
                </a>
              </div>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="mx-4 mt-3 px-5 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium text-center hover:bg-primary-600 transition-colors cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}